import React, { useEffect, useContext } from 'react';
import get from 'lodash/get';
import { useRouter } from 'next/router';
import Layout from '../../components/Layout/Layout';

import css from './ChooseRole.module.scss';
import { AnalyticsContext } from '../../ModularReduxAnalytics';

import { useApp, useAppDispatchers } from '../../state/app';
import { useUser, useUserDispatchers } from '../../state/user';
import { useToggles } from '../../state/toggles';
import Alert from '../../components/UI/Alert/index.tsx';

const ChooseRole = props => {
  const router = useRouter();
  const { tempPhoneNumber, partner, referralRewardValue, urlRole } = useUser();
  const { setRole, setNextRegStep, setUserIsTeen } = useUserDispatchers();
  const { analytics } = useContext(AnalyticsContext);
  const {
    featureToggles,
    noPhoneNumberPriceText,
    roleSelectCTA,
    acquisitionRoleBasedRedirect,
    defaultMax,
    defaultInfinity,
    corePlanCopy
  } = useToggles();
  const { invalidatePma } = useAppDispatchers();
  const { noPhoneRegEntry } = useApp();

  const maxFlow =
    get(featureToggles, 'planFlow.enabled') &&
    get(featureToggles, 'planFlow.planName') === 'max';
  const isMaxOrInfinityDefault = defaultMax.enabled || defaultInfinity.enabled;
  const design = maxFlow ? 'max' : 'default';
  const altChooseRoleText = noPhoneRegEntry || maxFlow;
  const shouldRenderShortHeaderVersion =
    isMaxOrInfinityDefault || corePlanCopy.enabled;

  const chooseRoleSubHeader = `Greenlight is the debit card for kids and money app for families.${
    shouldRenderShortHeaderVersion
      ? ''
      : ` All plans cover the whole family, up to 5 kids.`
  }`;

  const getRoleText = role => {
    if (roleSelectCTA.enabled && roleSelectCTA.variables?.shortText) {
      return role === 'parent' ? <p>Parent or Guardian</p> : <p>Kid or Teen</p>;
    }
    if (roleSelectCTA.enabled && !roleSelectCTA.variables?.shortText) {
      return role === 'parent' ? <p>I'm a parent</p> : <p>I'm a kid or teen</p>;
    }
    return role === 'parent' ? (
      <>
        <p>I'm the parent or guardian</p>
        <p>The debit card will be for my child</p>
      </>
    ) : (
      <>
        <p>I'm a kid or teen</p>
        <p>The debit card will be for me</p>
      </>
    );
  };

  const showOneYearAlert = () => {
    const maxRedemptionLimit = localStorage.getItem('maxRedemptionLimit');
    if (maxRedemptionLimit) {
      const isAtMaxRedemptionLimit = maxRedemptionLimit === 'true';
      return (
        <Alert
          heading=""
          message={
            isAtMaxRedemptionLimit
              ? 'Each Greenlight member can only give one subscription. It looks like this promo was already used.'
              : 'Finish signing up to get your free subscription.'
          }
          icon={isAtMaxRedemptionLimit ? 'error' : 'sparkle'}
          variation={isAtMaxRedemptionLimit}
          color={isAtMaxRedemptionLimit ? 'red' : 'green'}
          highlightText={!isAtMaxRedemptionLimit ? '$60 credit applied!' : ''}
        />
      );
    }
  };

  const showActionBaseIncentiveMessage = () => {
    const utmCampaign = JSON.parse(
      localStorage.getItem('adsSuperProperties')
    )?.utm_campaign;

    if (
      referralRewardValue &&
      utmCampaign === 'action_based_5_dollar_incentive'
    ) {
      return (
        <Alert
          heading={'Rewards pending'}
          icon={'greenCheck'}
          message={
            "Get your $30 reward and $5 child login bonus after your first month's fee"
          }
          color={'green'}
        />
      );
    }
  };

  const tempSetRole = role => {
    setRole(role);

    if (role === 'child') {
      invalidatePma();
    } else {
      router.replace({
        pathname: '/lta-redirect',
        query: {
          role: 'parent',
          phoneNumber: tempPhoneNumber
        }
      });
    }
  };

  const showRegistrationAlert = () => {
    const utmSource = JSON.parse(
      localStorage.getItem('adsSuperProperties')
    )?.utm_source;
    if (!utmSource || utmSource !== 'extole') {
      return <></>;
    }

    const maxRedemptionLimit = localStorage.getItem('maxRedemptionLimit');
    if (!maxRedemptionLimit) {
      showOneYearAlert(maxRedemptionLimit);
    }
    if (referralRewardValue) {
      return showActionBaseIncentiveMessage();
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.trackEvent('Registration Choose Role Viewed');
    analytics.trackEvent('Feature Toggle Decided', {
      eventProps: {
        feature_key: 'acquisition_corePlanCopy',
        variation_key: corePlanCopy.variationKey,
        toggle_enabled: corePlanCopy.enabled,
        experiment_key: 'acquisition_corePlanCopy_test'
      }
    });
    if (acquisitionRoleBasedRedirect.enabled) {
      if (urlRole === 'parent' || urlRole === 'child') {
        tempSetRole(urlRole);
      }
    }

    if (!tempPhoneNumber) {
      setNextRegStep('phone');
    }

    if (partner) {
      setRole('parent');
    }
  }, []);

  return (
    <>
      {maxFlow && (
        <img
          src="../../static/choose-role-max-banner.svg"
          alt="Greenlight Max"
          className={css.maxBannerMobile}
          data-testid="max-banner"
        />
      )}
      <Layout data-e2e="choose-role">
        <div className={css.chooseRoleWrapper}>
          {showRegistrationAlert()}
          {maxFlow && (
            <img
              src="../../static/choose-role-max-banner-desktop.svg"
              alt="Greenlight Max"
              className={css.maxBannerDesktop}
            />
          )}
          {altChooseRoleText ? (
            <>
              <h1 data-e2e="choose-role-header">
                What's your role in the family?
              </h1>
              {shouldRenderShortHeaderVersion && (
                <h2 data-e2e="choose-role-subheader">{chooseRoleSubHeader}</h2>
              )}
              {noPhoneNumberPriceText && (
                <h2 data-e2e="choose-role-subheader">
                  Greenlight is the debit card for kids and money app for families. All plans cover the whole family, up to 5 kids.
                </h2>
              )}
            </>
          ) : (
            <>
              <h1 data-e2e="choose-role-header">Welcome to Greenlight.</h1>
              <h2 data-e2e="choose-role-subheader">{chooseRoleSubHeader}</h2>
              <h3 data-e2e="choose-role-text">
                What's your role in the family?
              </h3>
            </>
          )}
          <div
            className={`${css.chooseRole} ${
              (noPhoneRegEntry || maxFlow) && css.addHeaderSpace
            } ${css[design]}`}
            onClick={() => tempSetRole('parent')}
            data-e2e="parent"
          >
            <img
              src={`../../static/parent-piggybank-${design}.svg`}
              alt="Parent role"
            />
            <div className={css.chooseRoleText}>{getRoleText('parent')}</div>
          </div>
          <div
            className={`${css.chooseRole} ${css[design]}`}
            onClick={() => tempSetRole('child')}
            data-e2e="child"
          >
            <img
              src={`../../static/child-piggybank-${design}.svg`}
              alt="Child role"
            />
            <div className={css.chooseRoleText}>{getRoleText('child')}</div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default ChooseRole;
